// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * prepareDiscussionDoc
 *
 * Write-time hook that parses `@mentions` from a discussion's `content`
 * field and populates `mentions[]` with the hybrid identifier+teamMemberId
 * shape (and optional `userId` for human users).
 *
 * Idempotent: re-running on a document that already has `mentions[]`
 * re-parses the content (covers content edits where the mention list
 * may have changed).
 *
 * Forward-only: existing rows are not migrated. Only writes that pass
 * through this hook get their mentions populated.
 */

import type { AgentLookupEntry } from './mentionResolver'
import { parseMentions } from './mentionParser'
import { resolveMentions } from './mentionResolver'
import type { DiscussionMention } from '../types'

/**
 * Mutate `doc.mentions` in place based on `doc.content`.
 *
 * - Empty / missing content → `mentions` left untouched (no key added).
 * - Non-empty content → `mentions` is set to the resolved array (replacing any prior value).
 *
 * Returns the same doc reference for ergonomic chaining in the caller's loop.
 */
export function prepareDiscussionDoc<
  D extends {
    content?: unknown
    mentions?: DiscussionMention[]
  },
>(doc: D, lookup: Map<string, AgentLookupEntry>): D {
  if (typeof doc.content !== 'string' || doc.content.length === 0) {
    return doc
  }

  const parsed = parseMentions(doc.content)
  doc.mentions = resolveMentions(parsed, lookup)
  return doc
}
